import axios from 'axios';

import { useVuelidate } from '@vuelidate/core';
import { required, numeric, between } from '@vuelidate/validators';

import { Chart as ChartJS, ArcElement, Tooltip, Legend, Title } from 'chart.js';
import { Pie } from 'vue-chartjs';

import { parseDataValue, defaultTooltipLabel } from './functions.js';
import colors from './colors.js';

ChartJS.register(ArcElement, Tooltip, Legend, Title);

export function createNomogramWidget({
    dataApi,
    fields,
    chartColors,
    payload,
    errorMessage,
    chartLabelProperty = 'label',
}) {
    return {
        setup() {
            return { v$: useVuelidate() };
        },
        components: {
            Pie,
        },
        data() {
            const state = {
                data: null,
                dataApi,
                dataChartTitle: '',
                error: false,
            };

            fields.forEach(field => {
                state[field.model] = field.defaultValue;
                state[field.minModel] = field.minDefault;
                state[field.maxModel] = field.maxDefault;
            });

            return state;
        },
        validations() {
            const validations = {};

            fields.forEach(field => {
                validations[field.model] = {
                    required,
                    numeric,
                    betweenValue: between(this[field.minModel], this[field.maxModel]),
                };
            });

            return validations;
        },
        computed: {
            hasData() {
                return this.data && this.data.risks && this.data.risks.length;
            },
            dataset() {
                if (!this.hasData) {
                    return { labels: [], datasets: [] };
                }

                return {
                    labels: this.data.risks.map(risk => risk[chartLabelProperty]),
                    datasets: [{
                        data: this.data.risks.map(risk => risk.percentage),
                        backgroundColor: chartColors,
                    }]
                };
            },
            options() {
                return {
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                        legend: {
                            position: 'right',
                            labels: {
                                boxWidth: 18,
                                font: {
                                    size: 18,
                                },
                                padding: 12,
                            },
                        },
                        title: {
                            display: true,
                            text: this.dataChartTitle,
                            font: {
                                size: 22,
                            },
                            color: colors.text,
                        },
                        tooltip: {
                            callbacks: {
                                label: defaultTooltipLabel(this),
                            },
                        }
                    },
                };
            }
        },
        methods: {
            async submit() {
                this.error = false;

                const result = await this.v$.$validate();
                if (!result) return;

                try {
                    const response = await axios.post(this.dataApi, payload(this));

                    if (!response || !response.data || !response.data.success) {
                        throw new Error(errorMessage);
                    }

                    this.data = response.data;
                }
                catch (error) {
                    console.error(error);
                    this.error = true;
                }
            }
        },
        mounted() {
            if (this.$el.dataset.api) {
                this.dataApi = this.$el.dataset.api;
            }

            if (this.$el.dataset.chartTitle) {
                this.dataChartTitle = this.$el.dataset.chartTitle;
            }

            fields.forEach(field => {
                const minValue = parseDataValue(this.$el.dataset[field.minDatasetKey], field.parser);
                const maxValue = parseDataValue(this.$el.dataset[field.maxDatasetKey], field.parser);
                const defaultValue = parseDataValue(this.$el.dataset[field.defaultDatasetKey], field.parser);

                if (minValue !== null) {
                    this[field.minModel] = minValue;
                }

                if (maxValue !== null) {
                    this[field.maxModel] = maxValue;
                }

                if (defaultValue !== null) {
                    this[field.model] = defaultValue;
                }
            });
        }
    };
}
